"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import type Lenis from "lenis";

let globalLenis: Lenis | null = null;

export function getGlobalLenis() {
  return globalLenis;
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Site-wide smooth scroll: a short, natural-feeling lag behind the wheel/touch
 * input (not a heavy parallax glide). `/projects` runs its own isolated Lenis
 * instance tied to the listing zoom (see listingLenis.ts) — mounting a second
 * one there would fight it for the wheel, so this bails out on that route.
 */
export function GlobalSmoothScroll() {
  const pathname = usePathname();
  const rafRef = useRef(0);

  useEffect(() => {
    if (pathname === "/" || pathname?.startsWith("/projects")) return;
    if (prefersReducedMotion()) return;

    let destroyed = false;
    let lenis: Lenis | null = null;

    (async () => {
      const { default: Lenis } = await import("lenis");
      if (destroyed) return;

      lenis = new Lenis({
        duration: 0.9,
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 1,
        allowNestedScroll: true,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      });
      globalLenis = lenis;

      const raf = (time: number) => {
        lenis?.raf(time);
        rafRef.current = requestAnimationFrame(raf);
      };
      rafRef.current = requestAnimationFrame(raf);
    })();

    return () => {
      destroyed = true;
      cancelAnimationFrame(rafRef.current);
      lenis?.destroy();
      if (globalLenis === lenis) globalLenis = null;
    };
  }, [pathname]);

  return null;
}
